import { useState, useEffect } from 'react'
import { Modal } from '../../../shared/components'
import { Collapsible } from '../../../shared/components/Collapsible'
import Button from '../../../shared/components/Button'
import Suits from "@/assets/suits.png"
import type { ProductDetailsModalProps } from '../types'
import { useCurrencyFormatter } from '../../../shared/utils/currencyUtils'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../auth/store'
import { merchantsApi } from '../../merchants/api/merchantsApi'

export const ProductDetailsModal = ({ isOpen, onClose, product, onEdit, onPublish, onDelete, isPublishing, isDeleting }: ProductDetailsModalProps) => {
  const [selectedSize, setSelectedSize] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const { formatPrice } = useCurrencyFormatter()

  const { user } = useAuthStore()
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE'
  const { data: merchantData } = useQuery({
    queryKey: ['merchants', product?.merchantId],
    queryFn: () => merchantsApi.get(product!.merchantId!),
    enabled: !!(isCollectiveStaff && product?.merchantId),
    staleTime: 2 * 60 * 1000,
  })
  
  // Initialize with first variants when product changes
  useEffect(() => {
    if (product && product.sizes && product.sizes.length > 0) {
      const firstSize = product.sizes[0]
      const firstMaterial = firstSize.materials?.[0]
      const firstColor = firstMaterial?.colours?.[0]
      
      setSelectedSize(firstSize.size.name)
      setSelectedMaterial(firstMaterial?.material.name || '')
      setSelectedColor(firstColor?.colour.name || '')
      setSelectedImageIndex(0)
    }
  }, [product])

  if (!product) return null

  // Extract sizes from product data or use default
  const sizes = product.sizes && product.sizes.length > 0
    ? product.sizes
    : []

  // Get current size data
  const currentSizeData = sizes.find(s => s.size.name === selectedSize)
  
  // Get available materials for current size
  const availableMaterials = currentSizeData?.materials || []
  
  // Get current material data
  const currentMaterialData = availableMaterials.find(m => m.material.name === selectedMaterial)
  
  // Get available colors for current size and material
  const availableColors = currentMaterialData?.colours || []
  
  // Get current color data
  const currentColorData = availableColors.find(c => c.colour.name === selectedColor)
  
  // Get images based on current variant
  const images = currentColorData?.imageUrls && currentColorData.imageUrls.length > 0 
    ? currentColorData.imageUrls 
    : [Suits]
  
  // Calculate total price with additional costs
  const calculateTotalPrice = () => {
    const basePrice = product?.basePrice || 0
    const sizePrice = currentSizeData?.additionalPrice || 0
    const materialPrice = currentMaterialData?.additionalPrice || 0
    const colorPrice = currentColorData?.additionalPrice || 0
    
    return {
      total: basePrice + sizePrice + materialPrice + colorPrice,
      breakdown: {
        base: basePrice,
        size: sizePrice,
        material: materialPrice,
        color: colorPrice
      }
    }
  }
  
  const priceInfo = calculateTotalPrice()
  const quantity = currentColorData?.quantity || 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-[23px] text-foreground font-[700]">Product Details</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className={`text-sm flex items-center ${product.status==="PUBLISHED" ? 'text-green-600' : 'text-red-600'}`}>
                {product.status === "PUBLISHED"  ? <>
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                    < path d="M8.5 13.167V3.83362" stroke="#03750B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M3.82812 8.50028L8.49479 3.83362L13.1615 8.50028" stroke="#03750B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>  Published </> :
                  <>
                    <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                      <path d="M7.80729 3.2498V5.41646H9.16146M8.44917 1.08313H5.91146C5.62414 1.08313 5.34859 1.19727 5.14543 1.40043C4.94226 1.6036 4.82813 1.87915 4.82812 2.16646V8.66646C4.82812 8.95378 4.94226 9.22933 5.14543 9.4325C5.34859 9.63566 5.62414 9.7498 5.91146 9.7498H10.2448C10.5321 9.7498 10.8077 9.63566 11.0108 9.4325C11.214 9.22933 11.3281 8.95378 11.3281 8.66646V3.92255C11.3281 3.77822 11.2993 3.63536 11.2433 3.50235C11.1873 3.36933 11.1052 3.24884 11.002 3.14796L9.20642 1.39188C9.00403 1.19399 8.73223 1.08317 8.44917 1.08313Z" stroke="#AB0303" stroke-width="1.08333" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M9.16406 9.74953V10.8329C9.16406 11.1202 9.04993 11.3957 8.84676 11.5989C8.6436 11.8021 8.36805 11.9162 8.08073 11.9162H3.7474C3.46008 11.9162 3.18453 11.8021 2.98136 11.5989C2.7782 11.3957 2.66406 11.1202 2.66406 10.8329V4.87453C2.66406 4.58721 2.7782 4.31166 2.98136 4.1085C3.18453 3.90534 3.46008 3.7912 3.7474 3.7912H4.83073" stroke="#AB0303" stroke-width="1.08333" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Draft
                  </>

                }
              </span>
            </div>
            {onEdit && (
              <Button
                onClick={() => onEdit(product.id)}
                variant='outline'
                className='w-[110px] text-[14px] text-foreground'
                size='sm'

              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.33594 2.66669H2.66927C2.31565 2.66669 1.97651 2.80716 1.72646 3.05721C1.47641 3.30726 1.33594 3.6464 1.33594 4.00002V13.3334C1.33594 13.687 1.47641 14.0261 1.72646 14.2762C1.97651 14.5262 2.31565 14.6667 2.66927 14.6667H12.0026C12.3562 14.6667 12.6954 14.5262 12.9454 14.2762C13.1955 14.0261 13.3359 13.687 13.3359 13.3334V8.66669" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12.3359 1.66684C12.6012 1.40162 12.9609 1.25262 13.3359 1.25262C13.711 1.25262 14.0707 1.40162 14.3359 1.66684C14.6012 1.93205 14.7502 2.29177 14.7502 2.66684C14.7502 3.04191 14.6012 3.40162 14.3359 3.66684L8.0026 10.0002L5.33594 10.6668L6.0026 8.00017L12.3359 1.66684Z" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Edit
              </Button>
            )}

            {onDelete && (
              <Button
                onClick={() => onDelete(product.id)}
                variant='outline'
                className='text-[14px] text-foreground'
                size='sm'
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="w-4 h-4 border-2 border-border border-t-red-600 rounded-full animate-spin"></div>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4H3.33333H14" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.6693 3.99998V13.3333C12.6693 13.6869 12.5288 14.0261 12.2787 14.2761C12.0287 14.5262 11.6896 14.6666 11.3359 14.6666H4.66927C4.31565 14.6666 3.97651 14.5262 3.72646 14.2761C3.47641 14.0261 3.33594 13.6869 3.33594 13.3333V3.99998M5.33594 3.99998V2.66665C5.33594 2.31302 5.47641 1.97389 5.72646 1.72384C5.97651 1.47379 6.31565 1.33331 6.66927 1.33331H9.33594C9.68956 1.33331 10.0287 1.47379 10.2787 1.72384C10.5288 1.97389 10.6693 2.31302 10.6693 2.66665V3.99998" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6.66406 7.33331V11.3333" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33594 7.33331V11.3333" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 ">
          {/* Left Column - Images */}
          <div>
            <div className="mb-4">
              <img
                src={images[selectedImageIndex] ?? Suits}
                alt={product.name}
                className="w-[90%] h-[388px] object-cover rounded-[10px]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {images.map((url, index) => (
                <img
                  key={index}
                  src={url}
                  alt={`${product.name} ${index + 1}`}
                  className={`w-[83.33px] h-[86.5px] object-cover rounded cursor-pointer ${selectedImageIndex === index ? 'ring-2 ring-black' : ''
                    }`}
                  onClick={() => setSelectedImageIndex(index)}
                />
              ))}
            </div>
          </div>

          {/* Right Column - Details */}
          <div className=" w-full">
            <div>
              <p className="text-[14px] text-foreground font-[600] mb-1">
                {product?.category?.name} {merchantData?.name ? ` • By ${merchantData.name}` : ''}
              </p>
              <h3 className="text-[33px] text-foreground font-[700] mb-2">{product?.name}</h3>
              <div>
                <p className="text-[18px] text-foreground font-[700]">{formatPrice(priceInfo.breakdown.base)}</p>
                {(priceInfo.breakdown.size > 0 || priceInfo.breakdown.material > 0 || priceInfo.breakdown.color > 0) && (
                  <p className="text-[12px] text-muted-foreground">
                    + {formatPrice(priceInfo.breakdown.size + priceInfo.breakdown.material + priceInfo.breakdown.color)}
                  </p>
                )}
              </div>
            </div>

            {/* Size Selection */}
            <div>
              <div className="flex justify-between items-center my-5">
                <span className="font-medium">Size</span>
                <button className="text-[16px] text-foreground font-[400] underline">Size Guide</button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {sizes && sizes.length > 0 ? (
                  sizes.map((size) => (
                    <button
                      key={size.size.id}
                      onClick={() => {
                        setSelectedSize(size.size.name)
                        // Reset to first material and color of new size
                        const firstMaterial = size.materials?.[0]
                        const firstColor = firstMaterial?.colours?.[0]
                        setSelectedMaterial(firstMaterial?.material.name || '')
                        setSelectedColor(firstColor?.colour.name || '')
                        setSelectedImageIndex(0)
                      }}
                      className={`py-2 px-3 text-sm border rounded ${
                        selectedSize === size.size.name
                          ? 'bg-black text-white border-black'
                          : 'border-border hover:border-gray-400'
                      }`}
                    >
                      {size.size.name}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground col-span-4">No sizes available</p>
                )}
              </div>
            </div>

            {/* Material Selection */}
            <div className="my-5">
              <span className="font-medium block mb-3">Material</span>
              <div className="flex flex-wrap gap-2">
                {availableMaterials.map((material) => (
                  <button
                    key={material.material.id}
                    onClick={() => {
                      setSelectedMaterial(material.material.name)
                      // Reset to first color of new material
                      const firstColor = material.colours?.[0]
                      setSelectedColor(firstColor?.colour.name || '')
                      setSelectedImageIndex(0)
                    }}
                    className={`py-2 px-3 text-sm border rounded ${
                      selectedMaterial === material.material.name
                        ? 'bg-black text-white border-black'
                        : 'border-border hover:border-gray-400'
                    }`}
                  >
                    {material.material.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <Collapsible title="Available color (s)" defaultOpen={true}>
              <div className="flex gap-6">
                {availableColors && availableColors.length > 0 ? (
                  availableColors.map((color) => (
                    <div key={color.colour.id} className="text-center">
                      <p className="text-xs mb-2">{color.colour.name}</p>
                      <button
                        onClick={() => {
                          setSelectedColor(color.colour.name)
                          setSelectedImageIndex(0)
                        }}
                        className={`w-[30px] h-[30px] rounded-full border-2 ${
                          selectedColor === color.colour.name ? 'border-gray-800' : 'border-border'
                        }`}
                        style={{ backgroundColor: color.colour.hexValue }}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No colors available for selected material</p>
                )}
              </div>
            </Collapsible>

            {/* Stock Info */}
            <div className="my-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Available Stock</span>
                <span className={`text-sm font-medium ${
                  quantity > 10 ? 'text-green-600' : quantity > 0 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {quantity} units
                </span>
              </div>
              {currentColorData?.weight && (
                <p className="text-sm text-muted-foreground mt-1">Weight: {currentColorData.weight}g</p>
              )}
            </div>

            {/* Product Details */}
            <Collapsible title="Product Details" defaultOpen={true}>
              <p className="text-muted-foreground">{product.description}</p>
            </Collapsible>

            {/* Stock Info */}
            <div className="my-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Production Duration</span>
                <span className={`text-sm font-medium`}>
                  {product?.productionTimeInDays} days
                </span>
              </div>
              {currentColorData?.weight && (
                <p className="text-sm text-muted-foreground mt-1">Weight: {currentColorData.weight}g</p>
              )}
            </div>

            {/* Variant Summary */}
            <Collapsible title="Selected Variant">
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Size:</span> {selectedSize}</p>
                <p><span className="font-medium">Material:</span> {selectedMaterial}</p>
                <p><span className="font-medium">Color:</span> {selectedColor}</p>
                <p><span className="font-medium">Stock:</span> {quantity} units</p>
                {currentColorData?.weight && (
                  <p><span className="font-medium">Weight:</span> {currentColorData.weight}g</p>
                )}
              </div>
            </Collapsible>

            {/* Action Button */}
            {onPublish && (
              <Button 
                className="w-full mt-5"
                onClick={() => onPublish(product.id)}
                disabled={isPublishing}
                loading={isPublishing}
              >
                {product.status === "PUBLISHED" ? 'Unpublish' : 'Publish'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}